import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { clearUserPath, getUserPath } from '../../hooks/useUserPathTracking';
import { GoogleAnalyticsEvent } from '../../models/app.models';
import {
  IContactSubmissionResult,
  IUserPathEntryComponent,
} from '../../models/strapi.models';
import { postStrapi } from '../../services/api.service';
import { logError } from '../../services/log.service';
import { sendGoogleAnalyticsEvent } from '../../shared/useGoogleAnalytics';
import { ContactStyle } from '../../styles/components/contact-us';

interface IContactState {
  submitResultType: 'success' | 'error' | undefined;
}

export interface IContactMethodProps {
  type: string;
  label: string;
  value?: string;
}

export interface IContactProps {
  contactMethods: IContactMethodProps[];
  formLabels: {
    title: string;
    subTitle: string;
    name: string;
    email: string;
    message: string;
    submit: string;
    submitSuccessMessage: string;
    submitErrorMessage: string;
  };
  darkMode?: boolean;
  titleTagName?: string;
  slot?: React.ReactNode;
}

const Contact = (props: IContactProps) => {
  const initialPageState: IContactState = {
    submitResultType: undefined,
  };
  const [state, setState] = useState(initialPageState);
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const router = useRouter();

  const onFormSubmitHandler = async (formData: any) => {
    // Populate all additional fields
    const userPath: IUserPathEntryComponent[] = getUserPath();
    formData = { ...formData, language: router.locale || 'en', userPath };

    // Clear user path after submission
    clearUserPath();

    const payload = {
      data: {
        ...formData,
        sourceUrl: window.location.href,
      },
    };

    try {
      const result: IContactSubmissionResult = await postStrapi<any>(
        `/contact-submissions`,
        payload
      );

      // Send the event after Strapi has accepted the submission. dataLayer is
      // also populated in development, where GTM itself is not loaded, so the
      // payload can be inspected locally without sending it to Google.
      const googleAnalitycsEvent: GoogleAnalyticsEvent = {
        name: 'generate_lead',
        params: {
          google_client_id: formData.google_client_id,
          google_user_id: formData.google_user_id,
          google_session_id: formData.google_session_id,
          gclid: formData.gclid,
          lead_timestamp: formData.lead_timestamp,
        },
      };

      if (result.iss) {
        // Obsfuscated spam detection ISS = true → Spam detected
        googleAnalitycsEvent.name = 'generate_lead_spam_submission_blocked';
      }

      sendGoogleAnalyticsEvent(googleAnalitycsEvent);

      const currentState = { ...state };
      currentState.submitResultType = 'success';
      setState(currentState);
    } catch (error) {
      const currentState = { ...state };
      currentState.submitResultType = 'error';
      setState(currentState);
      logError(['Error submitting contact form', error]);
    }
  };

  const contactMethodsIconsPaths: any = {
    phone: '/assets/images/icons/PhoneIcon.svg?v=1',
    email: '/assets/images/icons/EmailIcon.svg?v=1',
  };

  const Tag = props.titleTagName || 'h1';

  const getTitle = (title: string) => {
    return React.createElement(Tag, {}, title);
  };

  const DynamicContactForm = dynamic(
    () => import('../common/forms/ContactForm')
  );

  return (
    <div
      id="contact"
      className={`${ContactStyle['contact']} ${
        props.darkMode ? ContactStyle['dark-mode'] : ''
      }`}>
      <div className={ContactStyle['contact-heading']}>
        {getTitle(props.formLabels.title)}
        <p>{props.formLabels.subTitle}</p>
      </div>

      <div className={ContactStyle['contact-wrapper']}>
        {/* Contact form component wrapper */}
        <div className={ContactStyle['contact-form']}>
          <DynamicContactForm
            formSubmit={onFormSubmitHandler}
            isButtonDisabled={isButtonDisabled}
            setIsButtonDisabled={setButtonDisabled}
            darkMode={props.darkMode}
            submitResultType={state.submitResultType}
            labels={props.formLabels}
          />
        </div>
        {/* Contact form component wrapper */}

        {/* Contact methods wrapper */}
        <div className={ContactStyle['contact-methods']}>
          {props.contactMethods.map(
            (contactMethod: IContactMethodProps, index: number) => {
              return (
                <div
                  className={ContactStyle['contact-methods-item']}
                  key={index}>
                  <Image
                    quality={90}
                    className={ContactStyle['contact-methods-item-icon']}
                    src={contactMethodsIconsPaths[contactMethod.type]}
                    width={42}
                    height={42}
                    alt={contactMethod.type}
                  />
                  <div>
                    <h3>{contactMethod.label}</h3>
                    {!!contactMethod.value ? (
                      <p>{contactMethod.value}</p>
                    ) : null}
                  </div>
                </div>
              );
            }
          )}
        </div>
        {/* Contact methods wrapper */}
      </div>

      {props.slot && (
        <div className={ContactStyle['contact-wrapper']}>{props.slot}</div>
      )}
    </div>
  );
};

export default Contact;
